import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  StyleSheet,
  ScrollView,
  Platform,
  ActivityIndicator,
} from 'react-native';
import { StatusBar } from 'expo-status-bar';
import { useTheme } from '../context/ThemeContext';
import HamburgerMenu from '../components/HamburgerMenu';
import ProfileIcon from '../components/ProfileIcon';
import Stopwatch from '../components/Stopwatch';
import { getApiUrl } from '../utils/apiConfig';
import { localTodayIsoDate } from '../utils/dateFormat';
import {
  createLocationFeeRate,
  fetchLocationFeeRates,
  fetchTrainingClassLocations,
  formatClassMoney,
  locationDisplayName,
  updateTrainingClassLocation,
} from '../utils/classBookApi';

const LIGHT_CREAM = '#faf8f4';

export default function ClassBuilderPage({ navigation, route }) {
  const theme = useTheme();
  const styles = useMemo(() => getStyles(theme), [theme]);
  const [currentUser, setCurrentUser] = useState(route?.params?.user || null);
  const [locations, setLocations] = useState([]);
  const [selectedLocationId, setSelectedLocationId] = useState(null);
  const [showLocationDropdown, setShowLocationDropdown] = useState(false);
  const [loadingLocations, setLoadingLocations] = useState(true);
  const [loadingLocationDetail, setLoadingLocationDetail] = useState(false);
  const [savingLocation, setSavingLocation] = useState(false);
  const [savingFee, setSavingFee] = useState(false);
  const [statusMessage, setStatusMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  const [name, setName] = useState('');
  const [street, setStreet] = useState('');
  const [town, setTown] = useState('');
  const [city, setCity] = useState('');
  const [country, setCountry] = useState('');
  const [postcode, setPostcode] = useState('');
  const [locationStudents, setLocationStudents] = useState([]);
  const [feeExemptStudents, setFeeExemptStudents] = useState(() => new Set());

  const [feeAmount, setFeeAmount] = useState('');
  const [feeCurrency, setFeeCurrency] = useState('EUR');
  const [feeEffectiveFrom, setFeeEffectiveFrom] = useState(() => localTodayIsoDate());
  const [feeRates, setFeeRates] = useState([]);

  useEffect(() => {
    const uid = route?.params?.userId || currentUser?.id || 1;
    if (route?.params?.user) {
      setCurrentUser(route.params.user);
      return;
    }
    fetch(`${getApiUrl()}/api/users/${uid}`)
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => data && setCurrentUser(data))
      .catch(() => {});
  }, [route?.params?.user, route?.params?.userId, currentUser?.id]);

  const loadLocations = useCallback(async () => {
    setLoadingLocations(true);
    try {
      const rows = await fetchTrainingClassLocations();
      setLocations(rows);
    } catch (error) {
      console.error('Error loading class locations:', error);
      setLocations([]);
    } finally {
      setLoadingLocations(false);
    }
  }, []);

  useEffect(() => {
    void loadLocations();
  }, [loadLocations]);

  const applyLocationToForm = useCallback((location) => {
    setName(location?.Name ?? location?.name ?? '');
    setStreet(location?.Street ?? location?.street ?? '');
    setTown(location?.Town ?? location?.town ?? '');
    setCity(location?.City ?? location?.city ?? '');
    setCountry(location?.Country ?? location?.country ?? '');
    setPostcode(location?.Postcode ?? location?.postcode ?? '');
    const students = Array.isArray(location?.students) ? location.students : [];
    setLocationStudents(students);
    const exempt = Array.isArray(location?.fee_exempt_students) ? location.fee_exempt_students : [];
    setFeeExemptStudents(new Set(exempt));
  }, []);

  const loadLocationDetail = useCallback(
    async (locationId) => {
      if (locationId == null) return;
      setLoadingLocationDetail(true);
      setErrorMessage('');
      try {
        const location = locations.find((loc) => loc.id === locationId);
        if (location) applyLocationToForm(location);
        const rates = await fetchLocationFeeRates(locationId);
        setFeeRates(rates);
        const active = rates[0];
        if (active) {
          setFeeAmount(String(active.amount ?? ''));
          setFeeCurrency(active.currency || 'EUR');
          setFeeEffectiveFrom(active.effective_from || localTodayIsoDate());
        } else {
          setFeeAmount('');
          setFeeCurrency('EUR');
          setFeeEffectiveFrom(localTodayIsoDate());
        }
      } catch (error) {
        setErrorMessage(error?.message || 'Failed to load location details');
      } finally {
        setLoadingLocationDetail(false);
      }
    },
    [locations, applyLocationToForm]
  );

  useEffect(() => {
    if (selectedLocationId == null) return;
    void loadLocationDetail(selectedLocationId);
  }, [selectedLocationId, loadLocationDetail]);

  const selectedLocationLabel = useMemo(() => {
    const loc = locations.find((item) => item.id === selectedLocationId);
    return loc ? locationDisplayName(loc) : 'Select Training Class Location';
  }, [locations, selectedLocationId]);

  const toggleFeeExemptStudent = (studentName) => {
    setFeeExemptStudents((prev) => {
      const next = new Set(prev);
      if (next.has(studentName)) next.delete(studentName);
      else next.add(studentName);
      return next;
    });
  };

  const handleSaveLocation = async () => {
    if (selectedLocationId == null) return;
    const trimmedName = name.trim();
    if (!trimmedName) {
      setErrorMessage('Name is required');
      return;
    }
    setSavingLocation(true);
    setErrorMessage('');
    setStatusMessage('');
    try {
      const updated = await updateTrainingClassLocation(selectedLocationId, {
        Name: trimmedName,
        Street: street.trim() || null,
        Town: town.trim() || null,
        City: city.trim() || null,
        Country: country.trim() || null,
        Postcode: postcode.trim() || null,
        fee_exempt_students: [...feeExemptStudents],
      });
      await loadLocations();
      applyLocationToForm(updated);
      setStatusMessage('Location saved.');
    } catch (error) {
      setErrorMessage(error?.message || 'Failed to save location');
    } finally {
      setSavingLocation(false);
    }
  };

  const handleSaveFeeRate = async () => {
    if (selectedLocationId == null) return;
    const parsed = Number(String(feeAmount).replace(/,/g, '').trim());
    if (!Number.isFinite(parsed) || parsed < 0) {
      setErrorMessage('Enter a valid class fee amount');
      return;
    }
    setSavingFee(true);
    setErrorMessage('');
    setStatusMessage('');
    try {
      await createLocationFeeRate(selectedLocationId, {
        amount: parsed,
        currency: feeCurrency.trim().toUpperCase() || 'EUR',
        effective_from: feeEffectiveFrom.trim() || localTodayIsoDate(),
      });
      await loadLocationDetail(selectedLocationId);
      setStatusMessage('Class fee saved.');
    } catch (error) {
      setErrorMessage(error?.message || 'Failed to save class fee');
    } finally {
      setSavingFee(false);
    }
  };

  return (
    <View
      style={styles.container}
      data-screen="class-builder"
      data-theme={theme.isDarkMode ? 'dark' : 'light'}
    >
      <StatusBar style={theme.isDarkMode ? 'light' : 'dark'} />

      <View style={styles.header}>
        <View style={styles.hamburgerContainer}>
          <HamburgerMenu navigation={navigation} />
        </View>
        <View style={styles.breadcrumbContainer}>
          <Text style={styles.breadcrumb}>Class Builder</Text>
        </View>
        <View style={styles.profileIconContainer}>
          <ProfileIcon navigation={navigation} user={currentUser} />
        </View>
      </View>

      <ScrollView style={styles.content} contentContainerStyle={styles.contentContainer}>
        <Text style={styles.title}>Class Builder</Text>
        <Text style={styles.subtitle}>
          Manage class location addresses, default fees, and fee-exempt students.
        </Text>

        <Text style={styles.label}>Training Class Location</Text>
        <TouchableOpacity
          style={styles.dropdownTrigger}
          onPress={() => setShowLocationDropdown((open) => !open)}
          disabled={loadingLocations}
          {...(Platform.OS === 'web' ? { dataSet: { classBuilderLocationTrigger: 'true' } } : {})}
        >
          <Text style={styles.dropdownTriggerText}>
            {loadingLocations ? 'Loading locations…' : selectedLocationLabel}
          </Text>
          <Text style={styles.dropdownArrow}>{showLocationDropdown ? '▲' : '▼'}</Text>
        </TouchableOpacity>

        {showLocationDropdown && !loadingLocations ? (
          <ScrollView
            style={styles.dropdownList}
            nestedScrollEnabled
            keyboardShouldPersistTaps="handled"
            {...(Platform.OS === 'web' ? { dataSet: { classBuilderLocationList: 'true' } } : {})}
          >
            {locations.length === 0 ? (
              <Text style={styles.emptyDropdownText}>No locations yet</Text>
            ) : (
              locations.map((location) => (
                <TouchableOpacity
                  key={location.id}
                  style={styles.dropdownRow}
                  onPress={() => {
                    setSelectedLocationId(location.id);
                    setShowLocationDropdown(false);
                  }}
                  {...(Platform.OS === 'web' ? { dataSet: { classBuilderLocationRow: 'true' } } : {})}
                >
                  <Text style={styles.dropdownRowText}>{locationDisplayName(location)}</Text>
                </TouchableOpacity>
              ))
            )}
          </ScrollView>
        ) : null}

        {selectedLocationId != null ? (
          loadingLocationDetail ? (
            <ActivityIndicator style={styles.loader} color={theme.colors.primary || '#d4af37'} />
          ) : (
            <>
              <Text style={styles.sectionTitle}>Location address</Text>
              {[
                { label: 'Name', value: name, setter: setName, required: true },
                { label: 'Street', value: street, setter: setStreet },
                { label: 'Town', value: town, setter: setTown },
                { label: 'City', value: city, setter: setCity },
                { label: 'Country', value: country, setter: setCountry },
                { label: 'Postcode', value: postcode, setter: setPostcode },
              ].map((field) => (
                <View key={field.label} style={styles.fieldBlock}>
                  <Text style={styles.label}>
                    {field.label}
                    {field.required ? ' *' : ''}
                  </Text>
                  <TextInput
                    style={styles.input}
                    value={field.value}
                    onChangeText={field.setter}
                    editable={!savingLocation}
                    placeholder={field.label}
                    placeholderTextColor={
                      theme.isDarkMode ? 'rgba(255,255,255,0.45)' : 'rgba(0,0,0,0.45)'
                    }
                    {...(Platform.OS === 'web' ? { dataSet: { classBuilderInput: 'true' } } : {})}
                  />
                </View>
              ))}

              <Text style={styles.sectionTitle}>Fee exempt students</Text>
              <Text style={styles.hintText}>
                Exempt students are not charged for classes at this location (Eamon is exempt by default).
              </Text>
              <View style={styles.studentList}>
                {locationStudents.length === 0 ? (
                  <Text style={styles.emptyDropdownText}>No students linked to this location</Text>
                ) : (
                  locationStudents.map((studentName) => {
                    const selected = feeExemptStudents.has(studentName);
                    return (
                      <TouchableOpacity
                        key={studentName}
                        style={styles.studentRow}
                        onPress={() => toggleFeeExemptStudent(studentName)}
                        disabled={savingLocation}
                        {...(Platform.OS === 'web'
                          ? { dataSet: { classBuilderExemptRow: 'true' } }
                          : {})}
                      >
                        <Text style={styles.studentCheck}>{selected ? '☑' : '☐'}</Text>
                        <Text style={styles.studentName}>{studentName}</Text>
                      </TouchableOpacity>
                    );
                  })
                )}
              </View>

              <TouchableOpacity
                style={[styles.saveBtn, savingLocation && styles.saveBtnDisabled]}
                onPress={() => void handleSaveLocation()}
                disabled={savingLocation}
              >
                <Text style={styles.saveBtnText}>{savingLocation ? 'Saving…' : 'Save location'}</Text>
              </TouchableOpacity>

              <Text style={styles.sectionTitle}>Class fee</Text>
              <View style={styles.feeRow}>
                <View style={[styles.fieldBlock, styles.feeAmountField]}>
                  <Text style={styles.label}>Amount</Text>
                  <TextInput
                    style={styles.input}
                    value={feeAmount}
                    onChangeText={setFeeAmount}
                    editable={!savingFee}
                    keyboardType="decimal-pad"
                    placeholder="0.00"
                    placeholderTextColor={
                      theme.isDarkMode ? 'rgba(255,255,255,0.45)' : 'rgba(0,0,0,0.45)'
                    }
                    {...(Platform.OS === 'web' ? { dataSet: { classBuilderInput: 'true' } } : {})}
                  />
                </View>
                <View style={styles.fieldBlock}>
                  <Text style={styles.label}>Currency</Text>
                  <TextInput
                    style={[styles.input, styles.currencyInput]}
                    value={feeCurrency}
                    onChangeText={setFeeCurrency}
                    editable={!savingFee}
                    autoCapitalize="characters"
                    maxLength={3}
                    placeholder="EUR"
                    placeholderTextColor={
                      theme.isDarkMode ? 'rgba(255,255,255,0.45)' : 'rgba(0,0,0,0.45)'
                    }
                    {...(Platform.OS === 'web' ? { dataSet: { classBuilderInput: 'true' } } : {})}
                  />
                </View>
              </View>
              <View style={styles.fieldBlock}>
                <Text style={styles.label}>Effective from (YYYY-MM-DD)</Text>
                <TextInput
                  style={styles.input}
                  value={feeEffectiveFrom}
                  onChangeText={setFeeEffectiveFrom}
                  editable={!savingFee}
                  placeholder="YYYY-MM-DD"
                  placeholderTextColor={
                    theme.isDarkMode ? 'rgba(255,255,255,0.45)' : 'rgba(0,0,0,0.45)'
                  }
                  {...(Platform.OS === 'web' ? { dataSet: { classBuilderInput: 'true' } } : {})}
                />
              </View>

              <TouchableOpacity
                style={[styles.saveBtn, savingFee && styles.saveBtnDisabled]}
                onPress={() => void handleSaveFeeRate()}
                disabled={savingFee}
              >
                <Text style={styles.saveBtnText}>{savingFee ? 'Saving…' : 'Save class fee'}</Text>
              </TouchableOpacity>

              {feeRates.length > 0 ? (
                <View style={styles.feeHistoryBlock}>
                  <Text style={styles.sectionTitle}>Fee history</Text>
                  {feeRates.slice(0, 6).map((rate) => (
                    <Text key={rate.id} style={styles.feeHistoryRow}>
                      {rate.effective_from}: {formatClassMoney(rate.amount, rate.currency)}
                    </Text>
                  ))}
                </View>
              ) : null}
            </>
          )
        ) : null}

        {statusMessage ? <Text style={styles.successText}>{statusMessage}</Text> : null}
        {errorMessage ? <Text style={styles.errorText}>{errorMessage}</Text> : null}
      </ScrollView>

      <Stopwatch />
    </View>
  );
}

function getStyles(theme) {
  return StyleSheet.create({
    container: {
      flex: 1,
      backgroundColor: theme.colors.background,
    },
    header: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      paddingHorizontal: 16,
      paddingVertical: 12,
      borderBottomWidth: 1,
      borderBottomColor: theme.colors.border,
      backgroundColor: theme.colors.surface,
    },
    hamburgerContainer: {
      width: 40,
      backgroundColor: 'transparent',
    },
    breadcrumbContainer: {
      flex: 1,
      alignItems: 'center',
      backgroundColor: 'transparent',
    },
    breadcrumb: {
      fontSize: 18,
      fontWeight: '600',
      color: theme.colors.text,
      backgroundColor: 'transparent',
    },
    profileIconContainer: {
      width: 40,
      alignItems: 'flex-end',
      backgroundColor: 'transparent',
    },
    content: {
      flex: 1,
    },
    contentContainer: {
      padding: 20,
      paddingBottom: 40,
    },
    title: {
      fontSize: 28,
      fontWeight: '700',
      color: theme.colors.heading || theme.colors.primary || '#d4af37',
      marginBottom: 6,
      backgroundColor: 'transparent',
    },
    subtitle: {
      fontSize: 15,
      color: theme.colors.text,
      opacity: 0.8,
      marginBottom: 20,
      backgroundColor: 'transparent',
    },
    sectionTitle: {
      marginTop: 20,
      marginBottom: 10,
      fontSize: 18,
      fontWeight: '700',
      color: theme.colors.text,
      backgroundColor: 'transparent',
    },
    label: {
      fontSize: 14,
      fontWeight: '600',
      color: theme.colors.text,
      marginBottom: 8,
      backgroundColor: 'transparent',
    },
    dropdownTrigger: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: 12,
      borderRadius: 8,
      borderWidth: 1,
      borderColor: theme.colors.border,
      backgroundColor: theme.isDarkMode ? theme.colors.surface : LIGHT_CREAM,
    },
    dropdownTriggerText: {
      flex: 1,
      fontSize: 16,
      color: theme.colors.text,
      backgroundColor: 'transparent',
    },
    dropdownArrow: {
      marginLeft: 8,
      color: theme.colors.text,
      backgroundColor: 'transparent',
    },
    dropdownList: {
      maxHeight: 220,
      marginTop: 4,
      borderWidth: 1,
      borderColor: theme.colors.border,
      borderRadius: 8,
      backgroundColor: theme.isDarkMode ? theme.colors.surface : LIGHT_CREAM,
    },
    dropdownRow: {
      paddingVertical: 12,
      paddingHorizontal: 12,
      borderBottomWidth: 1,
      borderBottomColor: theme.colors.border,
      backgroundColor: 'transparent',
    },
    dropdownRowText: {
      fontSize: 15,
      color: theme.colors.text,
      backgroundColor: 'transparent',
    },
    emptyDropdownText: {
      padding: 12,
      color: theme.colors.text,
      opacity: 0.7,
      backgroundColor: 'transparent',
    },
    fieldBlock: {
      marginBottom: 12,
      backgroundColor: 'transparent',
    },
    input: {
      borderWidth: 1,
      borderColor: theme.colors.border,
      borderRadius: 8,
      paddingHorizontal: 12,
      paddingVertical: 10,
      fontSize: 16,
      color: theme.colors.text,
      backgroundColor: theme.isDarkMode ? theme.colors.background : '#FFFFFF',
    },
    hintText: {
      marginBottom: 8,
      fontSize: 13,
      color: theme.colors.text,
      opacity: 0.7,
      backgroundColor: 'transparent',
    },
    studentList: {
      borderWidth: 1,
      borderColor: theme.colors.border,
      borderRadius: 8,
      marginBottom: 12,
      backgroundColor: theme.isDarkMode ? theme.colors.background : '#FFFFFF',
    },
    studentRow: {
      flexDirection: 'row',
      alignItems: 'center',
      paddingVertical: 10,
      paddingHorizontal: 12,
      borderBottomWidth: 1,
      borderBottomColor: theme.colors.border,
      backgroundColor: 'transparent',
    },
    studentCheck: {
      width: 28,
      fontSize: 20,
      color: theme.colors.primary || '#d4af37',
      backgroundColor: 'transparent',
    },
    studentName: {
      flex: 1,
      fontSize: 15,
      color: theme.colors.text,
      backgroundColor: 'transparent',
    },
    feeRow: {
      flexDirection: 'row',
      gap: 10,
      backgroundColor: 'transparent',
    },
    feeAmountField: {
      flex: 1,
    },
    currencyInput: {
      width: 88,
      textAlign: 'center',
    },
    saveBtn: {
      marginTop: 8,
      alignSelf: 'flex-start',
      paddingVertical: 12,
      paddingHorizontal: 18,
      borderRadius: 8,
      backgroundColor: '#d4af37',
    },
    saveBtnDisabled: {
      opacity: 0.65,
    },
    saveBtnText: {
      fontSize: 15,
      fontWeight: '700',
      color: '#111',
      backgroundColor: 'transparent',
    },
    feeHistoryBlock: {
      marginTop: 8,
      backgroundColor: 'transparent',
    },
    feeHistoryRow: {
      fontSize: 14,
      color: theme.colors.text,
      marginBottom: 4,
      backgroundColor: 'transparent',
    },
    loader: {
      marginTop: 24,
    },
    successText: {
      marginTop: 16,
      color: theme.isDarkMode ? '#81c784' : '#2e7d32',
      backgroundColor: 'transparent',
    },
    errorText: {
      marginTop: 16,
      color: theme.isDarkMode ? '#e57373' : '#c62828',
      backgroundColor: 'transparent',
    },
  });
}
